import { useCallback, useMemo, useState, useSyncExternalStore } from 'react';
import { isString } from 'radash';
import { LayoutDisableReason, SizeType } from '@goldcast/player-config/src/types/asset-types';
import {
  canUseNewTimelineForUnmigratedLiveEvent,
  getClipFitFillDisabledReason,
  isSplitsInTimelineEnabledPure
} from '@goldcast/player-config/src/utils/clipUtils';
import { isFullVideoFaceRecEnabledForClip, isLayoutsWithVisionDataEnabled } from '@/stores/clip';
import { isAudioContent } from '@/stores/core';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { useTimelineSpeakers } from '@/hooks/useTimelineSpeakers';
import { shouldShowSplitsWarningDialog } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/hooks/useSplits';
import featureFlagStore from '@/stores/featureFlagStore';
import { FeatureFlagKeys } from '@/services/featureFlag';
const useClipLayoutPopupOptions = () => {
  const [hoveringAspectRatio, setHoveringAspectRatio] = useState<SizeType | null>(null);

  const { clipData, speakersAnalysis } = useClipsContext();

  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);

  const isSplitsLayoutDisabled = useMemo(() => {
    const splitsEnabledInTimeline = isSplitsInTimelineEnabledPure({
      clip: clipData,
      isSplitsTranslationEnabled: !!featureFlags[FeatureFlagKeys.Use_CL_Splits_Translations]
    });
    return splitsEnabledInTimeline && shouldShowSplitsWarningDialog(clipData, speakersAnalysis);
  }, [clipData, speakersAnalysis, featureFlags]);

  const canUseNewLayout = true;

  const onHoverAspectRatio = useCallback((aspectRatio: SizeType | null) => {
    setHoveringAspectRatio(aspectRatio);
  }, []);

  const { speakers, screenshareExists } = useTimelineSpeakers();

  const isSingleSpeaker = useMemo(() => {
    return speakers.length === 1;
  }, [speakers]);

  const isAudio = useMemo(() => {
    return isAudioContent();
  }, [clipData.content?.media_type]);

  /** To be only called for Uploads */
  const speakerAnalysisForUploadsAbsentReason = useMemo(() => {
    if (
      clipData.asset_metadata.duration &&
      clipData.asset_metadata.duration > 180 &&
      !isFullVideoFaceRecEnabledForClip(clipData)
    ) {
      return LayoutDisableReason.CLIP_EXCEEDS_MAX_DURATION;
    }

    if (speakersAnalysis.face_recognition_status === 'FAILED') {
      return LayoutDisableReason.SPEAKER_IDENTIFICATION_FAILED;
    }

    if (speakersAnalysis.accurate !== true) {
      return LayoutDisableReason.SPEAKER_IDENTIFICATION_IN_PROGRESS;
    }

    return null;
  }, [speakersAnalysis.accurate, speakersAnalysis.face_recognition_status, clipData.asset_metadata.duration]);

  const showMatchSpeakersCTA = useMemo(() => {
    return isLayoutsWithVisionDataEnabled(clipData) && !isAudio && !speakerAnalysisForUploadsAbsentReason;
  }, [clipData, isAudio, speakerAnalysisForUploadsAbsentReason]);

  const originalLayoutDisableReason = useMemo(() => {
    if (isAudio) {
      return LayoutDisableReason.AUDIO_CONTENT_NOT_SUPPORTED;
    }
    return null;
  }, [isAudio]);

  const isUnmigratedLiveEvent = useMemo(() => {
    return canUseNewTimelineForUnmigratedLiveEvent(clipData);
  }, [clipData]);

  const speakerLayoutsDisableReason = useMemo(() => {
    if (isAudio) {
      return LayoutDisableReason.AUDIO_CONTENT_NOT_SUPPORTED;
    }

    if (isUnmigratedLiveEvent) {
      return LayoutDisableReason.UNMIGRATED_LIVE_EVENT_DOESNT_SUPPORT_SPEAKER_LAYOUTS;
    }

    if (isSplitsLayoutDisabled) {
      return LayoutDisableReason.SPLITS_ENABLED_WITHOUT_FACE_REC;
    }

    if (isLayoutsWithVisionDataEnabled(clipData)) {
      return speakerAnalysisForUploadsAbsentReason;
    }

    return null;
  }, [isAudio, isUnmigratedLiveEvent, isSplitsLayoutDisabled, clipData, speakerAnalysisForUploadsAbsentReason]);

  const gridDisableReason = useMemo(() => {
    if (isString(speakerLayoutsDisableReason)) {
      return speakerLayoutsDisableReason;
    }
    if (isSingleSpeaker) {
      return LayoutDisableReason.GRID_NOT_SUPPORTED_FOR_SINGLE_SPEAKER;
    }
    return null;
  }, [speakerLayoutsDisableReason, isSingleSpeaker]);

  const focusDisableReason = useMemo(() => {
    if (isString(speakerLayoutsDisableReason)) {
      return speakerLayoutsDisableReason;
    }
    if (isSingleSpeaker && !screenshareExists) {
      return LayoutDisableReason.FOCUS_NOT_SUPPORTED_FOR_SINGLE_SPEAKER;
    }
    return null;
  }, [speakerLayoutsDisableReason, isSingleSpeaker, screenshareExists]);

  const pipDisableReason = useMemo(() => {
    if (isString(speakerLayoutsDisableReason)) {
      return speakerLayoutsDisableReason;
    }
    if (isSingleSpeaker && !screenshareExists) {
      return LayoutDisableReason.PIP_NOT_SUPPORTED_FOR_SINGLE_SPEAKER;
    }
    return null;
  }, [speakerLayoutsDisableReason, isSingleSpeaker, screenshareExists]);

  const fullScreenDisableReason = useMemo(() => {
    if (isString(speakerLayoutsDisableReason)) {
      return speakerLayoutsDisableReason;
    }
    return null;
  }, [speakerLayoutsDisableReason]);

  const fitFillDisabledReason = useMemo(() => {
    return getClipFitFillDisabledReason(clipData);
  }, [clipData]);

  return {
    canUseNewLayout,
    hoveringAspectRatio,
    onHoverAspectRatio,
    showMatchSpeakersCTA,
    originalLayoutDisableReason,
    gridDisableReason,
    focusDisableReason,
    pipDisableReason,
    fullScreenDisableReason,
    fitFillDisabledReason
  };
};

export default useClipLayoutPopupOptions;
